import React, { useEffect, useState } from "react";
import appleIcon from "../assets/icons/apple.svg";
import googleIcon from "../assets/icons/google.svg";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { cn } from "../lib/utils";

const STORE_LINKS = {
  ios: "https://apps.apple.com/us/app/google-authenticator/id388497605",
  android:
    "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
};

const detectPlatform = () => {
  if (typeof navigator === "undefined") {
    return "web";
  }

  const userAgent =
    navigator.userAgent || navigator.vendor || window.opera || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return "ios";
  }

  return "web";
};

const StoreOption = ({ icon, label, helperText, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-4 border border-[#EBE9EF] rounded-2xl p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] hover:text-white group"
  >
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6F5F9] group-hover:bg-white/20">
      <img src={icon} alt={label} className="h-6 w-6" />
    </span>
    <span className="flex flex-col items-start text-left">
      <span className="text-sm text-[#6D6584] group-hover:text-white/80">
        Download for
      </span>
      <span className="text-base font-semibold text-[#423E51] group-hover:text-white">
        {label}
      </span>
      <span className="text-xs text-[#9D96AF] group-hover:text-white/70">
        {helperText}
      </span>
    </span>
  </button>
);

const DownloadAuthAppButton = ({
  className = "",
  variant = "outline",
  buttonText = "Download Authentication App",
  ...buttonProps
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [platform, setPlatform] = useState("web");

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const openLink = (url) => {
    if (!url || typeof window === "undefined") {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleStoreLaunch = (storeKey) => {
    const validStores = ["ios", "android"];
    if (!validStores.includes(storeKey)) {
      console.error("Invalid store key:", storeKey);
      return;
    }
    const link = STORE_LINKS[storeKey];
    if (!link) {
      console.error("Invalid store key:", storeKey);
      return;
    }
    openLink(link);
    setIsModalOpen(false);
  };

  const handleDownloadClick = () => {
    if (platform === "ios") {
      openLink(STORE_LINKS.ios);
      return;
    }

    if (platform === "android") {
      openLink(STORE_LINKS.android);
      return;
    }

    setIsModalOpen(true);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        className={cn(
          "border-[1px] border-[#EBE9EF] text-[#6D6584] rounded-full font-semibold hover:opacity-90 hover:bg-transparent bg-transparent",
          className
        )}
        onClick={handleDownloadClick}
        {...buttonProps}
      >
        {buttonText}
      </Button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-full max-w-md rounded-3xl border-0 bg-white p-8 shadow-2xl">
          <DialogHeader className="space-y-3 text-center">
            <DialogTitle className="text-2xl font-semibold text-[#57249C]">
              Choose your authenticator
            </DialogTitle>
            <DialogDescription className="text-base text-[#6D6584]">
              Pick the store that matches your device to install Google
              Authenticator.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-4">
            <StoreOption
              icon={appleIcon}
              label="App Store"
              helperText="For iPhone & iPad"
              onClick={() => handleStoreLaunch("ios")}
            />
            <StoreOption
              icon={googleIcon}
              label="Google Play"
              helperText="For Android devices"
              onClick={() => handleStoreLaunch("android")}
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              className="text-[#6D6584]"
              onClick={() => setIsModalOpen(false)}
            >
              Maybe later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DownloadAuthAppButton;
